import { z } from "zod";
import { DateOnlySchema, IdSchema, IsoDateTimeSchema } from "../common/primitives.js";
const EVENT_KINDS = ["meeting", "site_visit", "deadline", "other"];
const EventKindSchema = z.enum(EVENT_KINDS);
const EVENT_SOURCES = ["manual", "task", "phase", "approval"];
const EventSourceSchema = z.enum(EVENT_SOURCES);
const EVENT_RESPONSES = ["needs_action", "accepted", "declined", "tentative"];
const EventResponseSchema = z.enum(EVENT_RESPONSES);
const RSVP_RESPONSES = ["accepted", "declined", "tentative"];
const RsvpResponseSchema = z.enum(RSVP_RESPONSES);
const EVENT_TITLE_MAX = 200;
const EVENT_DESCRIPTION_MAX = 5e3;
const EVENT_LOCATION_MAX = 300;
const EVENT_ATTENDEES_MAX = 50;
const EVENT_GUESTS_MAX = 20;
const EVENT_MAX_DAYS = 31;
const CALENDAR_RANGE_MAX_DAYS = 92;
const CALENDAR_ITEMS_MAX = 1e3;
const ICS_FEEDS_MAX = 10;
const EventParamsSchema = z.strictObject({ eventId: IdSchema });
const IcsFeedParamsSchema = z.strictObject({ feedId: IdSchema });
const EventAttendeeSchema = z.strictObject({
  /** A member of the organisation; null for a guest invited by email. */
  userId: IdSchema.nullable(),
  /** The member's display name; null for a guest. */
  displayName: z.string().nullable(),
  /** The guest's address; null for a member. */
  email: z.string().nullable(),
  response: EventResponseSchema
});
const CalendarEventSchema = z.strictObject({
  id: IdSchema,
  organizationId: IdSchema,
  organizationName: z.string(),
  spaceId: IdSchema,
  projectId: IdSchema.nullable(),
  projectCode: z.string().nullable(),
  projectName: z.string().nullable(),
  kind: EventKindSchema,
  source: EventSourceSchema,
  /** The task, phase or approval step a deadline event comes from; null for a manual event. */
  sourceId: IdSchema.nullable(),
  /** A deadline event is titled with its subject (task title, phase name or code, document title). */
  title: z.string(),
  description: z.string().nullable(),
  location: z.string().nullable(),
  startsAt: IsoDateTimeSchema,
  /** Exclusive for an all-day event (00:00 of the day after the last day). */
  endsAt: IsoDateTimeSchema,
  allDay: z.boolean(),
  startsOn: DateOnlySchema.nullable(),
  endsOn: DateOnlySchema.nullable(),
  ownerUserId: IdSchema.nullable(),
  ownerName: z.string().nullable(),
  /** The meeting held for this event (COM-BE-05). */
  meetingId: IdSchema.nullable(),
  attendees: z.array(EventAttendeeSchema).max(EVENT_ATTENDEES_MAX + EVENT_GUESTS_MAX),
  /** The caller's answer when they are invited; null otherwise. */
  myResponse: EventResponseSchema.nullable(),
  /** True for a deadline event: change its source instead. */
  readOnly: z.boolean(),
  /** The caller may edit or delete it: a manual event they organise, or in a space they manage. */
  canEdit: z.boolean(),
  version: z.int().min(1)
});
const windowIssues = (query, ctx) => {
  const from = Date.parse(query.from);
  const to = Date.parse(query.to);
  if (to <= from)
    ctx.addIssue({ code: "custom", path: ["to"], message: "`to` must be after `from`" });
  else if (to - from > CALENDAR_RANGE_MAX_DAYS * 864e5)
    ctx.addIssue({
      code: "custom",
      path: ["to"],
      message: `the window is at most ${String(CALENDAR_RANGE_MAX_DAYS)} days`
    });
};
const SpaceEventsQuerySchema = z.strictObject({ from: IsoDateTimeSchema, to: IsoDateTimeSchema }).superRefine(windowIssues);
const MyCalendarQuerySchema = z.strictObject({ from: IsoDateTimeSchema, to: IsoDateTimeSchema, orgId: IdSchema.optional() }).superRefine(windowIssues);
const CalendarEventListSchema = z.strictObject({
  items: z.array(CalendarEventSchema).max(CALENDAR_ITEMS_MAX),
  truncated: z.boolean()
});
const titleSchema = z.string().trim().min(1).max(EVENT_TITLE_MAX);
const descriptionSchema = z.string().trim().min(1).max(EVENT_DESCRIPTION_MAX);
const locationSchema = z.string().trim().min(1).max(EVENT_LOCATION_MAX);
const distinct = (items) => new Set(items).size === items.length;
const attendeeIdsSchema = z.array(IdSchema).max(EVENT_ATTENDEES_MAX).refine(distinct, "people must be distinct");
const guestEmailsSchema = z.array(z.email().max(254).toLowerCase()).max(EVENT_GUESTS_MAX).refine(distinct, "guests must be distinct");
function timingIssues(timing) {
  const issues = [];
  if (timing.allDay === true) {
    if (timing.startsOn === void 0) issues.push({ path: "startsOn", message: "required" });
    if (timing.endsOn === void 0) issues.push({ path: "endsOn", message: "required" });
    if (timing.startsAt !== void 0 || timing.endsAt !== void 0)
      issues.push({ path: "startsAt", message: "an all-day event has days, not times" });
    if (timing.startsOn !== void 0 && timing.endsOn !== void 0) {
      const days = (Date.parse(timing.endsOn) - Date.parse(timing.startsOn)) / 864e5;
      if (days < 0) issues.push({ path: "endsOn", message: "the last day is before the first" });
      else if (days >= EVENT_MAX_DAYS)
        issues.push({ path: "endsOn", message: `at most ${String(EVENT_MAX_DAYS)} days` });
    }
  } else {
    if (timing.startsAt === void 0) issues.push({ path: "startsAt", message: "required" });
    if (timing.endsAt === void 0) issues.push({ path: "endsAt", message: "required" });
    if (timing.startsOn !== void 0 || timing.endsOn !== void 0)
      issues.push({ path: "startsOn", message: "only an all-day event has days" });
    if (timing.startsAt !== void 0 && timing.endsAt !== void 0) {
      const ms = Date.parse(timing.endsAt) - Date.parse(timing.startsAt);
      if (ms < 0) issues.push({ path: "endsAt", message: "the end is before the start" });
      else if (ms > EVENT_MAX_DAYS * 864e5)
        issues.push({ path: "endsAt", message: `at most ${String(EVENT_MAX_DAYS)} days` });
    }
  }
  return issues;
}
const addTimingIssues = (timing, ctx) => {
  for (const issue of timingIssues(timing))
    ctx.addIssue({ code: "custom", path: [issue.path], message: issue.message });
};
const CreateEventRequestSchema = z.strictObject({
  kind: EventKindSchema.default("other"),
  title: titleSchema,
  description: descriptionSchema.optional(),
  location: locationSchema.optional(),
  allDay: z.boolean().default(false),
  startsAt: IsoDateTimeSchema.optional(),
  endsAt: IsoDateTimeSchema.optional(),
  startsOn: DateOnlySchema.optional(),
  endsOn: DateOnlySchema.optional(),
  attendeeUserIds: attendeeIdsSchema.default([]),
  guestEmails: guestEmailsSchema.default([])
}).superRefine(addTimingIssues);
const UpdateEventRequestSchema = z.strictObject({
  kind: EventKindSchema.optional(),
  title: titleSchema.optional(),
  description: descriptionSchema.nullable().optional(),
  location: locationSchema.nullable().optional(),
  allDay: z.boolean().optional(),
  startsAt: IsoDateTimeSchema.optional(),
  endsAt: IsoDateTimeSchema.optional(),
  startsOn: DateOnlySchema.optional(),
  endsOn: DateOnlySchema.optional(),
  attendeeUserIds: attendeeIdsSchema.optional(),
  guestEmails: guestEmailsSchema.optional()
}).superRefine((body, ctx) => {
  if (Object.keys(body).length === 0)
    ctx.addIssue({ code: "custom", message: "send at least one field to change" });
  const touchesTiming = body.allDay !== void 0 || body.startsAt !== void 0 || body.endsAt !== void 0 || body.startsOn !== void 0 || body.endsOn !== void 0;
  if (touchesTiming) addTimingIssues(body, ctx);
});
const EventResponseRequestSchema = z.strictObject({ response: RsvpResponseSchema });
const ICS_FEED_SCOPES = ["visible", "mine"];
const IcsFeedScopeSchema = z.enum(ICS_FEED_SCOPES);
const IcsFeedSchema = z.strictObject({
  id: IdSchema,
  organizationId: IdSchema,
  organizationName: z.string(),
  label: z.string().nullable(),
  scope: IcsFeedScopeSchema,
  includeExternal: z.boolean(),
  createdAt: IsoDateTimeSchema,
  revokedAt: IsoDateTimeSchema.nullable()
});
const IcsFeedListSchema = z.strictObject({ items: z.array(IcsFeedSchema) });
const CreateIcsFeedRequestSchema = z.strictObject({
  organizationId: IdSchema,
  label: z.string().trim().min(1).max(80).optional(),
  scope: IcsFeedScopeSchema.default("visible"),
  includeExternal: z.boolean().default(false)
});
const CreatedIcsFeedSchema = IcsFeedSchema.extend({ url: z.url() });
export {
  CALENDAR_ITEMS_MAX,
  CALENDAR_RANGE_MAX_DAYS,
  CalendarEventListSchema,
  CalendarEventSchema,
  CreateEventRequestSchema,
  CreateIcsFeedRequestSchema,
  CreatedIcsFeedSchema,
  EVENT_ATTENDEES_MAX,
  EVENT_DESCRIPTION_MAX,
  EVENT_GUESTS_MAX,
  EVENT_KINDS,
  EVENT_LOCATION_MAX,
  EVENT_MAX_DAYS,
  EVENT_RESPONSES,
  EVENT_SOURCES,
  EVENT_TITLE_MAX,
  EventAttendeeSchema,
  EventKindSchema,
  EventParamsSchema,
  EventResponseRequestSchema,
  EventResponseSchema,
  EventSourceSchema,
  ICS_FEEDS_MAX,
  ICS_FEED_SCOPES,
  IcsFeedListSchema,
  IcsFeedParamsSchema,
  IcsFeedSchema,
  IcsFeedScopeSchema,
  MyCalendarQuerySchema,
  RSVP_RESPONSES,
  RsvpResponseSchema,
  SpaceEventsQuerySchema,
  UpdateEventRequestSchema,
  timingIssues
};
//# sourceMappingURL=index.js.map